<script setup>
import { nextTick, onMounted, useId, watch } from "vue";
import { useRoute } from "vue-router";

import SvgIcon from "./SvgIcon.vue";

const props = defineProps({
  tabs: {
    type: Array,
    required: true,
    validator: (tabs) => {
      // Each tab needs a string id and label.
      if (
        !tabs.every((tab) => tab && typeof tab.id === "string" && typeof tab.label === "string")
      ) {
        return false;
      }
      // Ids and labels must each be unique.
      const ids = tabs.map((tab) => tab.id);
      const labels = tabs.map((tab) => tab.label);
      return new Set(ids).size === ids.length && new Set(labels).size === labels.length;
    },
  },
  // The active tab's `id`. Defaults to the first tab's id when undefined.
  modelValue: { type: String, default: undefined },
  // Array of tab ids that are disabled.
  disabledIds: { type: Array, default: () => [] },
  /** Mount all tabs, even if they're not visible (eg. for form validation) */
  mountAllTabs: { type: Boolean, default: false },
  // Opt-in deep-linking: when set, the active tab is initialised on mount from
  // `route.query[queryParam]` if it names a known tab (e.g. pi-web's "Configure
  // in cockpit" links with `?tab=kanbanMapping`). Otherwise the component stays
  // controlled by `modelValue`.
  queryParam: { type: String, default: undefined },
});
const emit = defineEmits(["update:modelValue"]);

const uid = useId();
const route = useRoute();

// Deep-linking: apply the tab named in `route.query[queryParam]` once it appears
// in `tabs`. `tabs` may be populated asynchronously (e.g. a conditional tab that
// only exists after the page loads its ALM tool), so we retry on `tabs` changes,
// not just on mount. Applied at most once so it never overrides a later manual
// tab switch; an absent/invalid id falls back to the normal default.
let queryTabApplied = false;
function applyQueryTab() {
  if (queryTabApplied || !props.queryParam) return;
  const wanted = route?.query?.[props.queryParam];
  if (typeof wanted !== "string") {
    queryTabApplied = true;
    return;
  }
  if (!props.tabs.some((tab) => tab.id === wanted)) return;
  queryTabApplied = true;
  if (!props.disabledIds.includes(wanted) && wanted !== activeTabId()) {
    emit("update:modelValue", wanted);
  }
}
onMounted(applyQueryTab);
watch(() => props.tabs, applyQueryTab);

// DOM-safe HTML ids tied to indices, not tab.id — tab ids may contain
// characters disallowed by HTML `id`. Internal-only; external API uses tab.id.
const tabHtmlId = (index) => `tab_${uid}_${index}`;
const tabPanelHtmlId = (index) => `tabpanel_${uid}_${index}`;

const activeTabId = () => props.modelValue ?? props.tabs[0]?.id;
const isActive = (tab) => tab.id === activeTabId();

function selectTab(tab) {
  if (props.disabledIds.includes(tab.id)) return;
  emit("update:modelValue", tab.id);
}

// Switch to the first enabled tab with an error, unless the current tab
// already has an error (users expect to stay and fix what they can see).
async function focusFirstErrorTab() {
  await nextTick();
  const current = props.tabs.find((t) => t.id === activeTabId());
  if (current?.hasError) return;
  const idx = props.tabs.findIndex((tab) => tab.hasError && !props.disabledIds.includes(tab.id));
  if (idx < 0) return;
  emit("update:modelValue", props.tabs[idx].id);
  await nextTick();
  document.getElementById(tabHtmlId(idx))?.focus();
}

// Switching tabs unmounts the panel the caller lives in, so move focus onto the
// tab itself — otherwise focus falls to <body> and the change is unannounced.
async function focusTab(id) {
  const idx = props.tabs.findIndex((tab) => tab.id === id);
  if (idx < 0 || props.disabledIds.includes(id)) return;
  emit("update:modelValue", id);
  await nextTick();
  document.getElementById(tabHtmlId(idx))?.focus();
}

defineExpose({ focusFirstErrorTab, focusTab });
</script>

<template>
  <div class="tabs">
    <div class="tab-list" role="tablist">
      <button
        v-for="(tab, index) in tabs"
        :id="tabHtmlId(index)"
        :key="tab.id"
        type="button"
        role="tab"
        :aria-selected="isActive(tab)"
        :aria-disabled="disabledIds.includes(tab.id) || undefined"
        :aria-controls="tabPanelHtmlId(index)"
        :disabled="disabledIds.includes(tab.id)"
        class="tab-btn"
        :class="{ active: isActive(tab), disabled: disabledIds.includes(tab.id) }"
        @click="selectTab(tab)"
      >
        {{ tab.label }}
        <SvgIcon
          v-if="tab.hasError"
          name="system/alert-triangle"
          width="16"
          height="16"
          class="tab-error-icon"
          role="img"
          aria-label="Tab contains validation errors"
        />
      </button>
    </div>
    <div
      v-for="(tab, index) in tabs"
      :id="tabPanelHtmlId(index)"
      :key="tab.id"
      role="tabpanel"
      :aria-labelledby="tabHtmlId(index)"
      :hidden="!isActive(tab)"
    >
      <slot v-if="mountAllTabs || isActive(tab)" :name="tab.id" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use "@/colors" as c;
@use "@/styles/layout";

// Tables in a panel pin their <thead> below both sticky rows.
.tabs {
  --table-header-top: calc(#{layout.$content-header-height} + #{layout.$tab-list-height});
}

.tab-list {
  position: sticky;
  top: layout.$content-header-height;
  z-index: 2;
  display: flex;
  gap: 20px;
  padding-left: layout.$content-pad;
  background-color: c.$background-ui;
  border-bottom: 1px solid c.$border-light;
}

.tab-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0 0 0.625rem;
  font-family: inherit;
  font-size: 0.8125rem;
  font-weight: 500;
  color: c.$text-subtle;
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  margin-bottom: -1px;
  cursor: pointer;
  outline: none;

  &:hover:not(.active) {
    color: c.$text;
  }

  &:focus-visible {
    outline: 2px solid c.$main;
    outline-offset: -2px;
  }

  &.active {
    color: c.$main;
    border-bottom-color: c.$main;
  }

  &.disabled,
  &:disabled {
    color: c.$text-disabled;
    cursor: not-allowed;
  }

  &.disabled:hover:not(.active) {
    color: c.$text-disabled;
  }
}

.tab-error-icon {
  flex-shrink: 0;
  color: c.$brand-danger;
}
</style>
